import { useCallback, useRef } from 'react';

/**
 * Sons organiques Bobine — Web Audio API
 * Pas de fichiers externes, ultra-léger.
 *
 * - cristal()  : tintement de cristal (validation/succès)
 * - papier()   : froissement de papier de soie (ouverture album)
 * - souffle()  : souffle d'air doux (suppression/corbeille)
 */
const useBobineSounds = () => {
  const ctxRef = useRef(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  /* ── Pluie d'or (validation / succès) — fichier audio réel ── */
  const pluieOrRef = useRef(null);
  const cristal = useCallback(() => {
    try {
      if (!pluieOrRef.current) {
        pluieOrRef.current = new Audio('/bobine-pluie-or.mp3');
        pluieOrRef.current.volume = 0.15;
      }
      pluieOrRef.current.currentTime = 0;
      pluieOrRef.current.play().catch(() => {});
    } catch { /* Audio non supporté */ }
  }, []);

  /* ── Froissement de papier de soie (ouverture album) ── */
  const papier = useCallback(() => {
    try {
      const ctx = getCtx();
      const now = ctx.currentTime;
      const duration = 0.25;

      // Bruit blanc filtré
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.3;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // Filtre passe-bande (craquement papier)
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(3000, now);
      bp.Q.setValueAtTime(1.5, now);

      // Filtre passe-haut (retirer les basses)
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.setValueAtTime(800, now);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.03);
      gain.gain.linearRampToValueAtTime(0.05, now + 0.08);
      gain.gain.linearRampToValueAtTime(0.07, now + 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      source.connect(bp).connect(hp).connect(gain).connect(ctx.destination);
      source.start(now);
      source.stop(now + duration);
    } catch { /* Audio non supporté */ }
  }, [getCtx]);

  /* ── Souffle d'air doux (suppression) ── */
  const souffle = useCallback(() => {
    try {
      const ctx = getCtx();
      const now = ctx.currentTime;
      const duration = 0.45;

      // Bruit doux
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.15;
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // Filtre passe-bas (son feutré, comme fermer un tiroir)
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(1200, now);
      lp.frequency.exponentialRampToValueAtTime(300, now + duration);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.06, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

      source.connect(lp).connect(gain).connect(ctx.destination);
      source.start(now);
      source.stop(now + duration);
    } catch { /* Audio non supporté */ }
  }, [getCtx]);

  return { cristal, papier, souffle };
};

export default useBobineSounds;
